import { Routes } from '@angular/router';
import { EntryComponent } from './entry/entry.component';
import { PartnerSsoForwardComponent } from './entry/partner-sso-forward.component';
import { RedirectEntryComponent } from './entry/redirect-entry.component';
import { SiteHomeComponent, TemplateHomeComponent } from './home';
import { AppFailureComponent } from './failure/app-failure.component';
import { AppInsufficientPermissionComponent } from './insufficient-permission/app-insufficient-permission.component';
import { SelectedSiteService } from '../selected-site.service';
import {
  AccountIdLoggedInCanActivate,
  SiteIdLoggedInCanActivateChild,
  UpgradeCTAGuard,
  wpCloudRedirectGuard,
} from '../guards';
import { SelectedAccountService } from '../selected-account.service';

export const routes: Routes = [
  {
    path: 'app-failure',
    component: AppFailureComponent,
  },
  {
    path: 'app-failure/:message',
    component: AppFailureComponent,
  },
  {
    path: 'app-failure/:message/:backLink',
    component: AppFailureComponent,
  },
  {
    path: 'app-insufficient-permission/:accountGroupId',
    component: AppInsufficientPermissionComponent,
  },
  {
    path: 'partnersso/:accountGroupId',
    component: PartnerSsoForwardComponent,
  },
  {
    path: 'entry/:accountGroupId',
    component: EntryComponent,
    canActivate: [AccountIdLoggedInCanActivate, wpCloudRedirectGuard],
    resolve: { accountGroupId: SelectedAccountService },
  },
  {
    path: 'create-from-template/:accountGroupId',
    loadChildren: () =>
      import('./create-from-template/create-from-template.module').then((m) => m.CreateFromTemplateModule),
    canActivate: [AccountIdLoggedInCanActivate],
    resolve: { accountGroupId: SelectedAccountService },
  },
  {
    path: 'create/:accountId/create/:siteId/job/:jobId/:siteType',
    loadChildren: () => import('./create-progress/create-progress.module').then((m) => m.CreateProgressModule),
    canActivate: [AccountIdLoggedInCanActivate],
  },
  {
    path: 'migrate/:accountGroupId',
    loadChildren: () => import('./migrate/migrate.module').then((m) => m.MigrateModule),
    canActivate: [AccountIdLoggedInCanActivate],
    resolve: { accountGroupId: SelectedAccountService },
  },
  {
    path: 'migrate/:accountId/migrate/:siteId/job/:jobId/:siteType',
    loadChildren: () =>
      import('./migrate/migrate-progress/migrate-progress.module').then((m) => m.MigrateProgressModule),
    canActivate: [AccountIdLoggedInCanActivate],
  },
  {
    path: 'create-animation/:stageId',
    loadChildren: () => import('./create-progress/create-progress.module').then((m) => m.CreateProgressModule),
  },
  {
    path: ':partnerId/templates/manage',
    loadChildren: () => import('./manage-templates/manage-templates.module').then((m) => m.ManageTemplatesModule),
  },
  {
    path: 'site/:siteId/templates/:partnerId/manage',
    loadChildren: () => import('./manage-templates/manage-templates.module').then((m) => m.ManageTemplatesModule),
    resolve: { siteId: SelectedSiteService },
  },
  {
    path: ':partnerId/templates/manage/create',
    loadChildren: () =>
      import('./create-new-template/create-new-template.module').then((m) => m.CreateNewTemplateModule),
  },
  {
    path: '',
    canActivateChild: [SiteIdLoggedInCanActivateChild],
    children: [
      {
        path: 'redirect/entry/:siteId',
        component: RedirectEntryComponent,
      },
      {
        path: 'site/:siteId/overview',
        component: SiteHomeComponent,
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId',
        redirectTo: 'site/:siteId/overview',
      },
      {
        path: 'template/:siteId/overview',
        component: TemplateHomeComponent,
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId',
        redirectTo: 'template/:siteId/overview',
      },
      {
        path: 'site/:siteId/restore/:jobId',
        loadChildren: () =>
          import('./restore-in-progress/restore-in-progress.module').then((m) => m.RestoreInProgressModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/import',
        loadChildren: () => import('./import/import.module').then((m) => m.ImportModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/domain/management',
        loadChildren: () => import('./domain/domain-manager.module').then((m) => m.DomainManagerModule),
        resolve: { siteId: SelectedSiteService },
        canActivate: [UpgradeCTAGuard],
      },
      {
        path: 'site/:siteId/backup/management',
        loadChildren: () => import('./backup/backup-manager.module').then((m) => m.BackupManagerModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId/backup/management',
        loadChildren: () => import('./backup/backup-manager.module').then((m) => m.BackupManagerModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/staging/management',
        loadChildren: () => import('./staging/staging.module').then((m) => m.StagingModule),
        resolve: { siteId: SelectedSiteService },
        canActivate: [UpgradeCTAGuard],
      },
      {
        path: 'site/:siteId/advanced',
        loadChildren: () => import('./advanced/advanced-page.module').then((m) => m.AdvancedPageModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/advanced/sftp-key/list',
        loadChildren: () => import('./sftp/sftp.module').then((m) => m.SftpModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId/advanced/sftp-key/list',
        loadChildren: () => import('./sftp/sftp.module').then((m) => m.SftpModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId/advanced',
        loadChildren: () => import('./advanced/advanced-page.module').then((m) => m.AdvancedPageModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId/advanced/php-logs',
        loadChildren: () => import('./error-logs/error-logs.module').then((m) => m.ErrorLogsModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/advanced/php-logs',
        loadChildren: () => import('./error-logs/error-logs.module').then((m) => m.ErrorLogsModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'template/:siteId/advanced/activity-logs',
        loadChildren: () =>
          import('./advanced/activity-logs/activity-logs-table/activity-logs-table.module').then(
            (m) => m.ActivityLogsTableModule,
          ),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/advanced/activity-logs',
        loadChildren: () =>
          import('./advanced/activity-logs/activity-logs-table/activity-logs-table.module').then(
            (m) => m.ActivityLogsTableModule,
          ),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/emails',
        loadChildren: () => import('./emails/emails.module').then((m) => m.EmailsModule),
        resolve: { siteId: SelectedSiteService },
      },
      {
        path: 'site/:siteId/analytics',
        loadChildren: () => import('./analytics/analytics.module').then((m) => m.AnalyticsModule),
        resolve: { siteId: SelectedSiteService },
      },
    ],
  },
];
